import { Story } from '../../types/story';

export const brusselsGallery: Story = {
  id: 'brussels-gallery',
  title: 'Vernissage',
  excerpt: 'An art opening in Brussels becomes the backdrop for a game of seduction between artist and admirer.',
  city: 'Brussels',
  publishDate: '2025-12-30',
  readTime: '11 min read',
  tags: ['♀♀', 'art', 'sophisticated', 'seduction'],
  categories: ['queer', 'romantic'],
  intensity: 2,
  content: `The gallery in Saint-Gilles was packed for the opening. I'd come alone, drawn by the artist's name—Margot Beaumont, a Belgian painter whose work I'd been following online for years. Her paintings were intimate, sensual without being explicit, capturing moments of desire in brushstrokes that felt almost alive.

I found her in the back room, standing before her largest piece—a woman's form emerging from shadow, all suggestion and mystery. She was younger than I'd imagined, maybe thirty, with paint still under her fingernails and an intensity in her eyes that made me understand why her work felt so alive.

"What do you see?" she asked in French, not looking at me.

I answered in English, my French too rusty for art criticism. "Longing. The moment before touch, when the anticipation is almost unbearable."

She turned then, studied me with the same intensity she'd given the painting. "You understand."

"I try."

"Most people see a naked woman. They miss the point entirely." She switched to English, her accent making every word sound like an invitation. "You're not from here."

"London. I came specifically for this."

"For my work?"

"Yes."

Something shifted in her expression. "Come with me. I want to show you something."

She led me through the crowd, up a narrow staircase to the gallery's second floor. It was quieter here, just a few pieces hung in a small room.

"These aren't for sale," she said. "They're too personal. But I wanted them in the show anyway."

The paintings were different from the ones downstairs—more explicit, more raw. Women together, bodies intertwined, faces lost in pleasure or pain or both.

"They're beautiful," I said.

"They're honest." She stood close to me, close enough that I could smell her perfume—something dark and woody. "I paint what I know. What I want. What I remember."

"And what do you remember?"

"That desire is better than satisfaction. That the chase is better than the catch. That the best moments are the ones right before everything changes."

She moved to stand in front of me, blocking my view of the paintings. "You came all the way from London for my work. Why?"

"Because it makes me feel something. In a world that's mostly numb, that's rare."

"What does it make you feel?"

"Seen. Like you've painted my thoughts."

She reached up, traced my jawline with one paint-stained finger. "And what are you thinking now?"

"That I should probably go back downstairs before I do something inappropriate."

"What if I want you to do something inappropriate?"

My breath caught. "Here? Now?"

"Why not? This is my show. My rules." She glanced at the door. "No one comes up here. They're all downstairs drinking cheap wine and pretending to understand art."

"And what would we do up here?"

"Whatever we want." She kissed me then, soft at first, then deeper. She tasted like champagne and possibility.

I pulled back. "I don't even know if you—"

"I paint women for a reason." She smiled. "Now stop thinking and kiss me again."

We moved together like we'd done this before, like the three years I'd spent admiring her work had created a familiarity that bypassed the usual awkwardness of first times. Her hands were confident, knowing exactly where to touch, how much pressure, when to tease and when to take.

She pushed me against the wall, between two of her paintings, and I had the surreal experience of being surrounded by her art while she kissed down my neck, unbuttoned my shirt, traced patterns on my skin that felt like brushstrokes.

"I've been watching you," she murmured against my collarbone. "Since you walked in. The way you looked at my work—like you were hungry for it. I wanted to know what else you were hungry for."

"This," I gasped as her hand slipped under my skirt. "Definitely this."

She laughed, low and pleased. "Good. Because I've been thinking about this since I saw you standing in front of my painting, understanding it in a way no one else did."

We didn't undress completely—too risky, too public, even in this quiet room. But she knew how to work around clothes, how to find the spaces between fabric and skin, how to make me forget we were in a gallery with a hundred people one floor below.

I tried to stay quiet, biting my lip as her fingers moved with the same precision she used with a paintbrush. But when I came, I couldn't help the small sound that escaped, quickly muffled against her shoulder.

She held me as I caught my breath, both of us listening for footsteps on the stairs that never came.

"Your turn," I said when I could speak again.

"Later. Come home with me after the show. I'll let you take your time."

"Promise?"

"I promise. But first, you have to go back downstairs and pretend you're just another admirer. Can you do that?"

"I don't know. I'm not a very good actress."

"Try. It'll make it hotter later, knowing we have a secret."

I straightened my clothes, checked my reflection in the glass of one of her paintings. I looked thoroughly debauched, but maybe that would just blend in at an art opening.

"Wait," she said as I reached the door. "What's your name?"

"Claire."

"Claire. I'm going to paint you. Not now, not tonight. But someday. When I can capture the way you looked at my work. The way you look at me."

"I'd like that."

"Good. Now go. Mingle. Pretend you don't know what my hands feel like. I'll find you when it's time to leave."

I went back downstairs, accepted a glass of wine from a server, tried to focus on the other paintings. But all I could think about was Margot upstairs, the promise of later, the way she'd looked at me like I was art worth studying.

She found me an hour later, when the crowd had thinned. "Ready?"

Her apartment was in Ixelles, a top-floor studio with north-facing windows and canvases everywhere. We didn't make it to the bedroom. We barely made it through the door.

She took her time, like she'd promised, learning my body with the same attention she gave her paintings. And when I finally left at dawn, she was already sketching, trying to capture something she'd seen in me.

I went back to London the next day, but we stayed in touch. Video calls that turned into virtual sessions, messages that were half art critique and half foreplay, plane tickets booked on impulse.

Six months later, I moved to Brussels. A year later, she finished the painting—me, standing in her gallery, looking at her work with an expression she called "the moment before everything changes."

It hangs in our bedroom now, a reminder that sometimes the best art is the kind you get to live inside.`
};
